import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './Engine.module.css'
import { useUser } from '../../contexts/userContext'
import { Select, SelectOption } from '../../common/components/Select/Select'
import { EngineRunState, getRunState, LearningGoal } from './EngineHelper'
import { InstructionsAndForm } from './InstructionsAndForm'
import { GeneratingStatus } from './GeneratingStatus'
import { LoginStep } from './LoginStep'
import {
  DEFAULT_MAP_OPTIONS,
  ENGINE_LANGUAGE_SELECT_DEFAULT_OPTION,
  ENGINE_LANGUAGE_SELECT_OPTIONS,
} from '../../common/constants'
import { SingleValue } from 'react-select'
import i18n from '../../i18n'
import { InteractiveStep } from './InteractiveStep'
import { GeneratedStep } from './GeneratedStep'
import { Button } from '../../common/components/button/Button'
import { useNotification } from '../../contexts/NotificationContext'
import { useConfirmation } from '../../contexts/ConfirmationContext'
import { Task } from '../../types/commonTypes'
import { getLearningGoals, getNextJsonSnippet, getTaskFromJson } from './EngineGoalsAndTaskParser'
import { GameForm } from '../GameEditor/types'
import { MapType } from '../../api/gameTypes'
import { useGame } from '../../contexts/GameContext'
import { sleep } from '../../util/functional'

enum EngineStep {
  LOGIN = 'LOGIN',
  SILENT_LOGIN = 'SILENT_LOGIN',
  FILL_FORM = 'FILL_FORM',
  GENERATE = 'GENERATE',
  INTERACTIVE = 'INTERACTIVE',
  FINAL = 'FINAL',
}

export type EngineForm = {
  gameName: string
  instructorToAdd: string
  generatorPin: string
  webSources: string
  freeText: string
  inputFiles: File[]
  language: 'English' | 'Finnish' | 'Portuguese' | 'Dutch' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Spanish'
  template: string
  targetGroup: string
  readyTasks: boolean
}

const TOTAL_STEPS_COUNT = 3

export const Engine: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useUser()
  const [mainStep, setMainStep] = useState(EngineStep.SILENT_LOGIN)
  const [stepIndex, setStepIndex] = useState(1)
  const [initialValues] = useState<Partial<EngineForm>>({
    gameName: 'Your game',
    instructorToAdd: user?.name,
    generatorPin: '',
    webSources: '',
    inputFiles: undefined,
    language: 'English',
    template: 'random',
    targetGroup: '',
    readyTasks: false,
  })
  const [formData, setFormData] = useState(initialValues)
  const intervalPoller = useRef(0)
  const [engineRunId, setEngineRunId] = useState(0)
  const [runState, setRunState] = useState<Partial<EngineRunState> | null>({
    fileUploaded: false,
    matchPairsCreated: false,
    multiChoiceCreated: false,
    checkBoxCreated: false,
    missingWordCreated: false,
    creativeCreated: false,
  })
  const [statusText, setStatusText] = useState(t('engine.general_status.start', 'Game not generated yet'))
  const [errorStatus, setErrorStatus] = useState<string | null>(null)
  const [gameId, setGameId] = useState(0)
  const { notifyError } = useNotification()
  const errorShown = useRef(false)
  const { requestConfirmation } = useConfirmation()
  const [has3D, setHas3D] = useState(false)
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language)
  const [roundsLeft, setRoundsLeft] = useState(0)
  const [interactiveRound, setInteractiveRound] = useState(1)
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [learningGoals, setLearningGoals] = useState< LearningGoal[]>([])
  const [taskSlots, setTaskSlots] = useState<{ [key: number]: Task }>({})
  const { createGame, createOrUpdateTask, gameData } = useGame()
  const [selectedGameBoardUrl, setSelectedGameBoardUrl] = useState<string | null>(null)
  const [gameCreated, setGameCreated] = useState(false)
  const tasksCreated = useRef(false)
  const [shouldPoll, setShouldPoll] = useState(true)
  const [autoPrompt, setAutoPrompt] = useState<string | null>(null)
  const [waitingGameBoard, setWaitingGameBoard] = useState(false)
  const [readyTasks, setReadyTasks] = useState(false)

  useEffect(() => {
    if (gameData?.gameId) {
      setGameId(gameData.gameId)
      if (!tasksCreated.current) {
        tasksCreated.current = true
        Object.values(taskSlots).forEach(async (task, index) => {
          await sleep(index * 200)
          createOrUpdateTask({ ...task, order: (task.order ?? 1) - 1 })
        })
        setTimeout(() => {
          setGameCreated(true)
        }, Object.values(taskSlots).length * 200 + 1000)
      }
    }
  }, [createOrUpdateTask, gameData, taskSlots])

  useEffect(() => {
    console.log('shouldPoll', shouldPoll)
    if (engineRunId > 0 && (shouldPoll || waitingGameBoard)) {
      intervalPoller.current = window.setInterval(async function () {
        const getIt = await fetch(`${process.env.REACT_APP_ENGINE_BACKEND}/engine_file?run_id=${engineRunId}`)
        const content = await getIt.text()
        const rState = getRunState(content, gameCreated, setShouldPoll)
        if (rState.error) {
          setErrorStatus(rState.error)
          window.clearInterval(intervalPoller.current)
        }

        if (rState.previewRounds && mainStep !== EngineStep.INTERACTIVE && mainStep !== EngineStep.FINAL) {
          setMainStep(EngineStep.INTERACTIVE)
          setStatusText(t('engine.general_status.customize', 'Game building blocks generated'))
        }
        if (selectedGameBoardUrl) {
          setTimeout(() => {
            setMainStep(EngineStep.FINAL)
          }, 1000)
        }
        if (rState.allReady || gameCreated) {
          window.clearInterval(intervalPoller.current)
        }
        setRunState(rState)
      }, 500)
    } else {
      if (intervalPoller.current) {
        window.clearInterval(intervalPoller.current)
      }
    }

    return () => {
      window.clearInterval(intervalPoller.current)
    }
  }, [engineRunId, mainStep, t, gameCreated, selectedGameBoardUrl, shouldPoll, waitingGameBoard])

  useEffect(() => {
    try {
      const needToAutoPrompt: string[] = []
      if (runState?.previewRounds) {
        const allReceivedAnswers = runState.previewRounds[runState.previewRounds.length - 1]
        console.log(allReceivedAnswers)
        let startIndex = 0
        let maxRounds = 100

        try {
          const lgs = getLearningGoals(startIndex, allReceivedAnswers)
          if (lgs.jsonObject) {
            setLearningGoals(lgs.jsonObject)
          } 
        } catch (e) {
          console.log(e)
        }
        
        while (startIndex > -1 && maxRounds-- > 0) {
          const next = getNextJsonSnippet(startIndex, allReceivedAnswers)
          if (next.jsonObject) {
            try {
              const task = getTaskFromJson(1, next.jsonObject, selectedTemplate.includes('3D'))
              if (typeof task === 'string' || task === null) {
                console.log('malformed task found: ' + task)
                if (task) needToAutoPrompt.push(task)
              } else {
                setTaskSlots((prev) => {
                  if (prev) {
                    return {
                      ...prev,
                      [task.order ?? 0]: task,
                    }
                  } else {
                    return {}
                  }
                })
              }
            } catch (e) {
              console.log('parsing failed for ', next.jsonObject.taskType)
              needToAutoPrompt.push(next.jsonObject.taskType)
            }
          }
          startIndex = next.nextStartIndex
        }
      }
      if (needToAutoPrompt && !shouldPoll) {
        console.log('\n-----\nSETTING AUTO PROMPT NOW')
        setAutoPrompt(needToAutoPrompt.join(' ,'))
      }
    } catch (e) {
      console.log('Failed to parse tasks')
      console.log(e)
    }
  }, [runState?.previewRounds, selectedTemplate, shouldPoll])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [mainStep])

  const handleStartAgain = useCallback(() => {
    if (intervalPoller.current) window.clearInterval(intervalPoller.current)
    setTimeout(() => {
      errorShown.current = false
    }, 2000)
    setEngineRunId(0)
    setErrorStatus(null)
    setStepIndex(1)
    setMainStep(EngineStep.LOGIN)
    setStatusText(t('engine.general_status.start', 'Game not generated yet'))
    window.location.reload()
  }, [t])

  useEffect(() => {
    if (errorShown.current) return
    if (errorStatus?.includes('Invalid') || errorStatus?.includes('not found')) {
      errorShown.current = true
      handleStartAgain()
      notifyError({
        customDismissLabel: t('engine.error.try_again', 'Try again'),
        title: t('engine.error.pin_or_account.title', 'Pin or account not found'),
        content: (
          <div className={styles.notificationContent}>
            {t(
              'engine.error.pin_or_account.content',
              'We weren’t able to verify your PIN or account. Please double-check and try again or reach out if you need support!',
            )}
          </div>
        ),
      })
    } else if (errorStatus?.includes('No rounds left')) {
      errorShown.current = true
      handleStartAgain()
      notifyError({
        title: t('engine.error.no_rounds.title', 'Out of rounds!'),
        content: (
          <div className={styles.notificationContent}>
            {t(
              'engine.error.no_rounds.content',
              'Your attempts have been used up. Reach out to our support and get more!',
            )}
          </div>
        ),
      })
    } else if (errorStatus) {
      errorShown.current = true
      handleStartAgain()
      notifyError({
        customDismissLabel: t('engine.error.try_again', 'Try again'),
        title: t('engine.error.generic.title', 'Uh-oh! It seems I need a moment'),
        content: (
          <div className={styles.notificationContent}>
            {t(
              'engine.error.generic.content',
              'An unexpected error occurred, but don’t worry — no engine rounds were lost! Please wait 5 minutes and try again.',
            )}
          </div>
        ),
      })
    }
  }, [errorStatus, handleStartAgain, notifyError, t])

  const handleSubmit = useCallback(
    async (values: Partial<EngineForm>) => {
      if (mainStep === EngineStep.LOGIN || mainStep === EngineStep.SILENT_LOGIN) {
        const pinResponse = await fetch(
          `https://seppo-proxy.herokuapp.com/${process.env.REACT_APP_OLD_UI_BASE_URL}/engine_pin_check_only.json?email=${values.instructorToAdd}&pin_code=${values.generatorPin}`,
        )
        const reply = await pinResponse.text()
        if (!reply.includes('OK')) {
          setErrorStatus(reply)
          setMainStep(EngineStep.LOGIN)
          return
        }
        if (reply.includes('HAS_3D')) setHas3D(true)
        setFormData({ ...formData, ...values })
        try {
          const roundsLeft = JSON.parse(reply).rounds_left
          setRoundsLeft(roundsLeft)
          setStatusText(
            t('engine.general_status.start', 'Game not generated yet') +
              (roundsLeft > 0
                ? ' - ' + t('engine.general_status.rounds_left', 'Rounds left in your license:') + ' ' + roundsLeft
                : ''),
          )
        } catch (e) {}
        setMainStep(EngineStep.FILL_FORM)
        setStepIndex(2)
      } else {
        setFormData({ ...formData, ...values })
        setReadyTasks(values.readyTasks ? true : false)
        setMainStep(EngineStep.INTERACTIVE)
        // Do the actual submitting here
        const xhr = new XMLHttpRequest()
        xhr.open('POST', `${process.env.REACT_APP_ENGINE_BACKEND}/engine_submit`)
        xhr.onload = function (event: any) {
          if (event.target.response.includes('Error occurred')) {
            setErrorStatus(event.target.response)
          } else {
            const roundsLeft = parseInt(event.target.response.split('rounds left: ')[1])
            setRoundsLeft(roundsLeft)
            const temp =
              t('engine.general_status.generating', 'Game is being generated...') +
              ' ' +
              (isNaN(roundsLeft)
                ? ''
                : t('engine.general_status.rounds_left', 'Rounds left in your license:') + ' ' + roundsLeft)
            setStatusText(temp)
            setEngineRunId(event.target.response.split('=')[1])
          }
        }
        const engineForm = document.getElementById('ENGINE_FORM') as HTMLFormElement

        const formData2 = new FormData()

        formData2.append('gameName', values.gameName!)
        formData2.append('instructorToAdd', values.instructorToAdd!)
        formData2.append('generatorPin', values.generatorPin!)
        formData2.append('language', values.language!)
        formData2.append('template', values.template!)
        formData2.append('webSources', values.webSources!)
        formData2.append('freeText', values.freeText!)
        formData2.append('targetGroup', values.targetGroup!)
        formData2.append('readyTasks', values.readyTasks ? 'true' : 'false')

        if (values.inputFiles) {
          values.inputFiles.forEach((file, index) => {
            formData2.append('inputFiles', file)
          })
        }
        if (engineForm) {
          xhr.send(formData2)
        }
      }
    },
    [formData, mainStep, t],
  )

  useEffect(() => {
    if (user && user.enginePin && mainStep === EngineStep.SILENT_LOGIN) {
      handleSubmit({ instructorToAdd: user.name, generatorPin: user.enginePin })
    }
  }, [handleSubmit, user, mainStep])

  const handleUserPrompt = useCallback(
    (prompt: string) => {
      setStatusText(t('engine.general_status.interactive', 'Finetuning the game to perfection'))
      var xhr = new XMLHttpRequest()
      xhr.open(
        'GET',
        `${
          process.env.REACT_APP_ENGINE_BACKEND
        }/engine_prompt?round=${interactiveRound}&run_id=${engineRunId}&prompt=${encodeURIComponent(prompt)}`,
      )
      xhr.send()
      setInteractiveRound((prev) => prev + 1)
    },
    [engineRunId, interactiveRound, t],
  )

  const handleRequestGameBoard = useCallback(
    (prompt: string) => {
      setStatusText(t('engine.general_status.interactive_game_board', 'Waiting for game board generation'))
      var xhr = new XMLHttpRequest()
      if (formData.template?.includes('3D')) {
        //For the 3D games there is no image prompting
        xhr.open(
          'GET',
          `${
            process.env.REACT_APP_ENGINE_BACKEND
          }/engine_prompt?round=${interactiveRound}&run_id=${engineRunId}&prompt=${encodeURIComponent('CENCEL_NOW')}`,
        )
      } else {
        xhr.open(
          'GET',
          `${
            process.env.REACT_APP_ENGINE_BACKEND
          }/engine_prompt?round=${interactiveRound}&run_id=${engineRunId}&prompt=${encodeURIComponent(
            'IMAGE_PROMPT' + prompt,
          )}`,
        )
      }
      xhr.send()
      setWaitingGameBoard(true)
      setShouldPoll(true)
      setTimeout(() => {
        setWaitingGameBoard(true)
        setShouldPoll(true)
      }, 20000)
      setInteractiveRound((prev) => prev + 1)
    },
    [engineRunId, interactiveRound, t, formData.template],
  )

  const handleCreateGame = useCallback(async () => {
    setStatusText(t('engine.general_status.creating_game', 'Creating the final game'))

    const keywords = ['ENGPIN_' + formData.generatorPin, '']
    if (formData.template?.includes('3D')) {
      keywords.push('Seppo 3D Game')
      if (formData.template === 'news3D') {
        keywords.push(`ENGN_3D${process.env.REACT_APP_NEWS_ID}`)
      }
      if (formData.template === 'esg3D') {
        keywords.push(`ENGN_3D${process.env.REACT_APP_ESG_ID}`)
      }
      if (formData.template === 'nature3D') {
        keywords.push(`ENGN_3D${process.env.REACT_APP_NATURE_ID}`)
      }
      if (formData.template === 'shop3D') {
        keywords.push(`ENGN_3D${process.env.REACT_APP_SHOP_ID}`)
      }
    }

    const gameBasics: GameForm = {
      name: formData.gameName ?? 'Name',
      keywords,
      gameBoardSettings: {
        gameBoardType: MapType.STATIC,
        gameBoards: [{ fileName: 'Board1', name: 'Board 1', url: selectedGameBoardUrl! }],
        mapOptions: { center: [50, 50], zoom: DEFAULT_MAP_OPTIONS.zoom },
      },
      notificationSettings: {},
      advancedSettings: {},
    }

    const success = await createGame(gameBasics)
    if (!success) {
      notifyError({
        title: t('game_editor.create_game_error_notification.title', 'Failed to create game'),
        content: t(
          'game_editor.create_game_error_notification.content',
          'An error occurred while creating a new game. Please try again or contact us for support.',
        ),
      })
    }
    await fetch(`${process.env.REACT_APP_ENGINE_BACKEND}/engine_kill?run_id=${engineRunId}`)
  }, [
    createGame,
    formData.gameName,
    formData.generatorPin,
    formData.template,
    notifyError,
    selectedGameBoardUrl,
    t,
    engineRunId,
  ])

  const validate = useCallback(
    (values: EngineForm): Partial<EngineForm> => {
      if (mainStep === EngineStep.LOGIN) {
        return {
          generatorPin: !values.generatorPin ? t('engine.value_required', 'Required field') : undefined,
          instructorToAdd: !values.instructorToAdd ? t('engine.value_required', 'Required field') : undefined,
        }
      }
      if (mainStep === EngineStep.FILL_FORM) {
        if (values.template) {
          setSelectedTemplate(values.template)
        }
        return {
          gameName: !values.gameName ? t('engine.value_required', 'Required field') : undefined,
          template: !values.template ? t('engine.value_required', 'Required field') : undefined,
        }
      }
      return {}
    },
    [mainStep, t],
  )

  const handleChangeLanguage = (selectOption: SingleValue<SelectOption>) => {
    i18n.changeLanguage(selectOption?.value ?? ENGINE_LANGUAGE_SELECT_DEFAULT_OPTION.value)
    setCurrentLanguage(selectOption?.value ?? ENGINE_LANGUAGE_SELECT_DEFAULT_OPTION.value)
    if (mainStep === EngineStep.LOGIN || mainStep === EngineStep.FILL_FORM) {
      setStatusText(
        t('engine.general_status.start', 'Game not generated yet') +
          (roundsLeft > 0
            ? ' - ' + t('engine.general_status.rounds_left', 'Rounds left in your license:') + ' ' + roundsLeft
            : ''),
      )
    } else if (mainStep === EngineStep.INTERACTIVE) {
      setStatusText(t('engine.general_status.customize', 'Game building blocks generated'))
    } else if (mainStep === EngineStep.GENERATE) {
      setStatusText(t('engine.general_status.generating', 'Game is being generated...'))
    } else if (mainStep === EngineStep.FINAL) {
      setStatusText(t('engine.general_status.final', 'Game saved to Seppo Dashboard'))
    }
  }

  const cancelGeneration = useCallback(async () => {
    const confirm = await requestConfirmation({
      title: t('engine.confirm_cancel_title', 'Confirm canceling'),
      text: t('engine.confirm_cancel_text', 'Are you sure you want to cancel the game generation?'),
      confirmActionText: t('engine.confirm_cancel_button_text', 'Confirm'),
    })
    if (!confirm) {
      return false
    }
    if (engineRunId > 0) {
      await fetch(`${process.env.REACT_APP_ENGINE_BACKEND}/engine_kill?run_id=${engineRunId}`)
    }
    handleStartAgain()
  }, [engineRunId, handleStartAgain, requestConfirmation, t])

  const handleAddLearningGoal = useCallback((goal: LearningGoal) => {
    setLearningGoals((prev) => prev.concat(goal))
  }, [])

  const handleRemoveLearningGoal = useCallback((goal: LearningGoal) => {
    setLearningGoals((prev) => prev.filter(g => g.title !== goal.title))
  }, [])

  const language =
    ENGINE_LANGUAGE_SELECT_OPTIONS.find((option) => option.value === i18n.language) ??
    ENGINE_LANGUAGE_SELECT_DEFAULT_OPTION

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <div className={styles.statusText}>{statusText}</div>
        <div className={styles.language}>
          {t('onboarding_wizard.language', 'Language')}
          {': '}
          <Select
            options={ENGINE_LANGUAGE_SELECT_OPTIONS}
            value={language}
            onChange={handleChangeLanguage}
            className={styles.languageSelect}
          />
        </div>
      </div>
      <div className={styles.container} style={mainStep === EngineStep.INTERACTIVE ? {width: '100%', maxWidth: '100%'} : {}}>
        <div className={styles.top}>
          <img
            style={{ width: '90px' }}
            alt='Seppo'
            src='https://s3-eu-west-1.amazonaws.com/smartfeet-assets-production/assets/seppo.png'
          />
        </div>
        <div className={styles.mainAreaContainer}>
          {mainStep === EngineStep.LOGIN && (
            <LoginStep user={user} onSubmit={handleSubmit} validate={validate} initialValues={formData} />
          )}
          {[EngineStep.FILL_FORM, EngineStep.SILENT_LOGIN].includes(mainStep) && (
            <InstructionsAndForm
              onSubmit={handleSubmit}
              validate={validate}
              initialValues={formData}
              has3D={has3D}
              currentLanguage={currentLanguage}
            />
          )}
          {mainStep === EngineStep.GENERATE && <GeneratingStatus runState={runState} language={currentLanguage} />}
          {mainStep === EngineStep.INTERACTIVE && (
            <InteractiveStep
              selectedGameBoardUrl={selectedGameBoardUrl ?? null}
              gameBoardUrls={runState?.gameBoardUrls ?? null}
              previewRounds={runState?.previewRounds}
              onUserPromt={handleUserPrompt}
              onCreateGame={handleCreateGame}
              onRequestGameBoard={handleRequestGameBoard}
              onSelectGameBoard={setSelectedGameBoardUrl}
              learningGoals={learningGoals}
              onAddLearningGoal={handleAddLearningGoal}
              onRemoveLearningGoal={handleRemoveLearningGoal}
              taskSlots={taskSlots}
              template={formData.template}
              setShouldPoll={setShouldPoll}
              autoPrompt={autoPrompt}
              setAutoPrompt={setAutoPrompt}
              isPolling={shouldPoll}
              usage={runState?.usage}
              readyTasks={readyTasks}
            />
          )}
          {mainStep === EngineStep.FINAL && (
            <GeneratedStep
              runState={runState}
              gameCreated={gameCreated}
              gameName={formData.gameName ?? ''}
              gameId={gameId}
              gameMapUrl={selectedGameBoardUrl ?? ''}
              language={currentLanguage}
            />
          )}
        </div>
        <div className={styles.stepDisplay}>
          {t('onboarding_wizard.step', 'Step')} {stepIndex} / {TOTAL_STEPS_COUNT}
        </div>
        {stepIndex === 2 && (
          <div className={styles.stepDisplay}>
            <Button variant='outline-tiny' onClick={cancelGeneration}>
              {t('engine.cancel', 'Cancel generation')}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
